<template>
  <div class="integrity__events">
    <div class="d-flex justify-content-end">
      <button
        class="ui-action-button"
        :disabled="isUpdating"
        @click="save"
      >
        {{$t('global.saveChanges')}}
        <b-spinner class="ml-1" small label="loading" v-if="isUpdating"></b-spinner>
      </button>
    </div>

    <div class="integrity__eventsIntro">
      <strong class="integrity__eventsIntro--label">{{ $t('integrityCompliance.integrity.eventsIntro') }}</strong>
      <div class="wysiwyg-container">
        <div class="wysiwyg">
          <Wysiwyg
            id="integrityEvents"
            ref="integrityEvents"
            :articleBody="originalDescription"
            :placeholder="$t('integrityCompliance.integrity.form_write_content')"
            :includeMediaContent="false"
            v-on:updateBody="description = $event"
          ></Wysiwyg>
          <div class="ui-input-error-msg">
            <span v-if="$v.description.$error">{{ $t("global.inputs.required") }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Integrity Forum subcategory-->
    <FileUploaderByYear
      class="integrity__subcategory"
      :title="$t('integrityCompliance.integrity.integrity_forum')"
      :key="`${integrityKey.integrityForum}_${subcategoryKey}`"
      v-if="!isLoading"
      :subcategory="getSubCategoryByName(integrityKey.integrityForum)"
      @updatedFiles="onUpdatedFiles"
    />

    <!-- Coffe date subcategory-->
    <FileUploaderByYear
      class="integrity__subcategory"
      :title="$t('integrityCompliance.integrity.coffeeDate')"
      :key="`${integrityKey.coffeeDate}_${subcategoryKey}`"
      :allowedNumberOfFiles="3"
      :filenameRequired="true"
      :filenameLabel="$t('integrityCompliance.integrity.directorName')"
      v-if="!isLoading"
      :subcategory="getSubCategoryByName(integrityKey.coffeeDate)"
      @updatedFiles="onUpdatedFiles"
      :showErrors="showErrors"
    />

  </div>
</template>

<script>
const Wysiwyg = () => import("@/components/Wysiwyg");
const FileUploaderByYear = () => import("@/components/IntegrityCompliance/FileUploaderByYear");

import API_SERVICE from "@/lib/api/IntegrityCompliance.js";
const api = new API_SERVICE();

import { integrityKey, integrityComplianceKey, ICfilesName } from "@/lib/constants.js";
import { validateBody } from '@/lib/utils.js';

import S3_Handler from "@/lib/api/s3_handler";
const s3 = new S3_Handler();

export default {
  inject: ['IC_getMainCategory'], // data from main category "Integrity"
  props: {
    detail: {
      type: Object,
      default: () => { // this contains data of "events" subcategory
        return {
          id: 0, // number
          name: '',
          title: '',
          description: { ops: []},
          is_parent: false // boolean
        }
      }
    }
  },
  components: {
    Wysiwyg,
    FileUploaderByYear
  },
  validations: {
    description: {
      isRequired: function(data) {
        return validateBody(data)
      }
    }
  },
  async mounted () {
    this.categoryData = this.IC_getMainCategory();
    this.description = this.detail.description && this.detail.description.ops
      ? this.detail.description
      : this.originalDescription
    this.originalDescription = this.description;
    await this.getSubCategories();
    await this.setYearsAndSemesterBySubcategories(this.subcategories);
  },
  data: function() {
    return {
      isLoading: false,
      isUpdating: false,
      originalDescription: {
        ops: []
      },
      description: {
        ops: []
      },
      subcategories: [], //integrityForum, coffeeDate
      integrityKey,
      subcategoryKey: new Date().getTime(),
      subcategoryFiles: {},
      categoryData: {},
      showErrors: false
    }
  },
  methods: {
    async getSubCategories () {
      if(!this.detail.id) return
      try {
        this.isLoading = true;
        const { subcategories } = await api.getSubcategory(this.detail.id);
        if (subcategories && subcategories.length) {
          this.subcategories = subcategories;
          this.subcategoryKey = new Date().getTime();
        } else {
          this.$store.commit("global/SET_ALERT", {
            msg: this.$t('integrityCompliance.integrity.events_getError'),
            show: true,
            type: "error"
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.integrity.events_getError'),
          show: true,
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    },
    async setYearsAndSemesterBySubcategories (subcategories) {
      try {
        const subcateogriesYearsAndSemester = {};
        for (const subcategory of subcategories) {
          const yearsAndSemesters = await api.getYearsAndSemestersBySubcategory(subcategory.id)
          const years = yearsAndSemesters && yearsAndSemesters.length
            ? yearsAndSemesters.map(({ year }) => year)
            : []

          subcateogriesYearsAndSemester[subcategory.name] = {
            years
          };
        }

        const originalSubcategories = [...this.subcategories];
        this.subcategories = originalSubcategories.map(subcategory => {
          return {
            ...subcategory,
            years: subcateogriesYearsAndSemester[subcategory.name].years
          }
        });
      } catch (error) {
        throw error;
      }
    },
    getSubCategoryByName (categoryName) {
      const subcategories = this.subcategories && this.subcategories.length
        ? this.subcategories
        : [];
      const subcategory = subcategories.find(({ name }) => name === categoryName);
      return subcategory && subcategory.id ? subcategory : {}
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$error) return;
      const isValidFiles = this.isValidFiles();
      if (!isValidFiles) {
        this.showErrors = true;
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.integrity.events_coffeDate_fileNameRequired'),
          show: true,
          type: "error"
        });
        return;
      }

      try {
        this.isUpdating = true;
        await this.saveSubcategory(
          this.detail.id,
          integrityKey.events,
          {
            description: this.description,
            is_parent: true
          }
        )
        await this.saveSubcategoryFiles();

        this.$emit('subcategoryUpdated', {
          ...this.detail,
          description: this.description
        })
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('global.successSaved'),
          show: true,
          type: "success"
        });
      } catch (error) {
        console.error(error);
        this.$store.commit("global/SET_ALERT", {
          msg: this.$t('integrityCompliance.integrity.events_saveError'),
          show: true,
          type: "error"
        });
      } finally {
        this.showErrors = false;
        this.subcategoryKey = new Date().getTime();
        this.isUpdating = false;
      }
    },
    isValidFiles () {
      let erroredFiles = []
      const subcategoriesName = Object.keys(this.subcategoryFiles);
      if(subcategoriesName.length) {
        for(const subcategoryKey of subcategoriesName) {
          const subcategoryFiles = this.subcategoryFiles[subcategoryKey];
          const { files } = subcategoryFiles;
          const errored = files.filter(subcategoryFile => subcategoryFile.errored);
          if (errored && errored.length) {
            erroredFiles = erroredFiles.concat(errored);
            break;
          }
        }
      }

      return erroredFiles.length > 0 ? false : true
    },
    onUpdatedFiles (data) {
      const { subcategoryId, subcategoryName, subcategoryFiles } = data;
      const files = subcategoryFiles.map(subcategoryFile => {
        if (subcategoryName === integrityKey.coffeeDate) { // check for filename
          const errored = Boolean(
            (Boolean(subcategoryFile.file) || subcategoryFile.document_id) && 
            !subcategoryFile.name.trim()
          )
          return {
            ...subcategoryFile,
            errored
          }
        } else {
          return {
            ...subcategoryFile,
            errored: false
          }
        }
      })
      this.subcategoryFiles[subcategoryName] = {
        subcategoryId,
        files
      }
    },
    async saveSubcategoryFiles () {
      // if files are added, upload them to S3 then call api to log documents 
      const subcategoriesName = Object.keys(this.subcategoryFiles);
      if(subcategoriesName.length) {
        for(const subcategoryKey of subcategoriesName) {
          const subcategoryFiles = this.subcategoryFiles[subcategoryKey];
          const { subcategoryId, files } = subcategoryFiles;
          const filesToUploadToS3 = files.filter(subcategoryFile => subcategoryFile.file);

          let uploadedS3Files = [];
          if (filesToUploadToS3 && filesToUploadToS3.length) { // there are new files to upload to S3
            uploadedS3Files = await this.uploadSubcategoryFilesToS3({
              subcategoryId,
              subcategoryKey,
              subcategoryFiles: filesToUploadToS3
            });
          }

          // only update existing files from subcategory coffeDate due to the name must to be updated regardless file is uploaded or not
          const existingFilesToUpdate = files.filter(fileData => 
            (fileData.document_id && subcategoryKey === integrityKey.coffeeDate) 
            && !fileData.file
          ).map(file => {
            return {
              id: file.document_id,
              name: file.name,
              description: `${ICfilesName[integrityComplianceKey.integrity][subcategoryKey]} ${file.year}`,
              srcBucket: s3.getBucketName(),
              src: s3.getS3KeyFromUrl(file.file_url),
              category_id: this.categoryData.id,
              subcategory_id: subcategoryId,
              year: file.year
            }
          });

          uploadedS3Files = uploadedS3Files.concat(existingFilesToUpdate);

          // to save files linked to years, update subcategory is necessary
          const subcategoryInfo = this.getSubCategoryByName(subcategoryKey);
          const subcategoryYears = subcategoryInfo.years && subcategoryInfo.years.length ? subcategoryInfo.years : [];

          const yearsAdded = files.map(({ year }) => year);

          let updatedYears = false;
          yearsAdded.every(year => {
            if (!subcategoryYears.includes(year)) {
              updatedYears = true;
              return false;
            }
            return true;
          })

          if (updatedYears) {
            await this.saveSubcategory(
              subcategoryInfo.id,
              subcategoryKey, // integrityForum || coffeeDate
              {
                description: { ops: [] },
                category_id: this.categoryData.id, //integrity
                parent_id: this.detail.id, //events
                is_parent: false,
                years: Array.from(new Set([...subcategoryYears, ...yearsAdded]))
              }
            );
          }
          this.subcategoryFiles[subcategoryKey].files = [];
          await this.saveFiles(uploadedS3Files);
        }
      }
    },
    async uploadSubcategoryFilesToS3 (subcategoryData) {
      const { subcategoryId, subcategoryKey, subcategoryFiles } = subcategoryData;
      if (!subcategoryFiles.length) return;
      const uploadedS3Files = await Promise.all(
        subcategoryFiles.map(async(subcategoryFile) => {
          try {
            const { document_id, year } = subcategoryFile;
            const { Bucket, Key } = await s3.uploadFile(
              subcategoryFile.path,
              subcategoryFile.file
            );

            return {
              id: document_id,
              name: subcategoryFile.name || `${ICfilesName[integrityComplianceKey.integrity][subcategoryKey]} ${year}`,
              description: `${ICfilesName[integrityComplianceKey.integrity][subcategoryKey]} ${year}`,
              srcBucket: Bucket,
              src: Key,
              category_id: this.categoryData.id,
              subcategory_id: subcategoryId,
              year
            }

          } catch (error) {
            console.error(error);
          }
        })
      )
      return uploadedS3Files;
    },
    async saveSubcategory (subcategoryId, subcategoryName, customParams) {
      try {
        const params = {
          name: subcategoryName,
          title: ICfilesName[integrityComplianceKey.integrity][subcategoryName],
          description: {},
          ...customParams
        }

        const response = await api.updateSubcategory(subcategoryId ,params)
        return response;

      } catch (error) {
        console.error(error)
      }
    },
    async saveFiles (files) {
      for(const fileData of files) {
        try {
          if (fileData.id) {
            await api.updateDocument(fileData.id, fileData);
          } else {
            await api.createDocument(fileData);
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.integrity {
  &__events {
    width: 100%;
  }
  &__eventsIntro {
    margin-top: $global_padding;
    .wysiwyg-container {
      margin: $global_padding 0;
    }
  }
  &__subcategory {
    margin-top: $global_padding;
  }
}
</style>
